import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MapTrifold, Warehouse, Info, User } from "@phosphor-icons/react";

/**
 * Admin dashboard block — active chantiers plotted on OpenStreetMap.
 *
 * Depot (siège) is a blue warehouse pin; each active chantier is a coloured
 * dot (status-tinted) with a hover tooltip previewing the fiche and a click
 * that opens the full chantier page.
 */
const STATUS_META = {
  waiting: { label: "En attente", color: "#9CA3AF" },
  in_progress: { label: "En cours", color: "#005CFF" },
  urgent: { label: "Urgent", color: "#EF4444" },
  done: { label: "Terminé", color: "#10B981" },
};

// Blue warehouse divIcon for the depot marker
const depotIcon = L.divIcon({
  className: "",
  html: `<div style="width:34px;height:34px;border-radius:10px;background:linear-gradient(135deg,#005CFF,#0047CC);border:2px solid #7CB2FF;box-shadow:0 0 18px rgba(0,92,255,0.55);display:flex;align-items:center;justify-content:center;font-size:16px;color:#fff;">🏢</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const FALLBACK_CENTER = [43.6108, 3.9662]; // Saint-Aunès area (BKPRO)

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `il y a ${Math.round(diff)} s`;
  if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
  return `il y a ${Math.round(diff / 86400)} j`;
}

export default function ChantiersMapPanel() {
  const nav = useNavigate();
  const [depot, setDepot] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const [d, c, t] = await Promise.all([
        api.get("/depot").catch(() => ({ data: null })),
        api.get("/chantiers").catch(() => ({ data: [] })),
        api.get("/users/team-locations").catch(() => ({ data: [] })),
      ]);
      if (!mounted) return;
      setDepot(d.data);
      setChantiers(c.data || []);
      setTeam(t.data || []);
      setLoading(false);
    };
    load();
    // Refresh only team positions (cheap + moving target) every 5 min —
    // matches the client-side beacon cadence, no point polling faster.
    const timer = setInterval(async () => {
      try {
        const r = await api.get("/users/team-locations");
        if (mounted) setTeam(r.data || []);
      } catch {
        /* silent — never disturb the dashboard */
      }
    }, 300_000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  // Active = not done, and has usable coordinates
  const geoChantiers = useMemo(
    () =>
      chantiers.filter(
        (c) => c.status !== "done" && typeof c.latitude === "number" && typeof c.longitude === "number",
      ),
    [chantiers],
  );

  // We only plot **live** technicians on the dashboard map — offline pins
  // clutter the view and confuse admins about who's actually en route.
  // Full history (including offline) stays available in Tracés / Team dialog.
  const geoTeam = useMemo(
    () =>
      team.filter(
        (r) => r.latitude != null && r.longitude != null && r.is_live,
      ),
    [team],
  );
  const totalWithCoords = useMemo(
    () => team.filter((r) => r.latitude != null && r.longitude != null).length,
    [team],
  );
  const liveCount = geoTeam.length;

  const center = useMemo(() => {
    if (depot?.latitude != null && depot?.longitude != null) return [depot.latitude, depot.longitude];
    if (geoChantiers.length) return [geoChantiers[0].latitude, geoChantiers[0].longitude];
    if (geoTeam.length) return [geoTeam[0].latitude, geoTeam[0].longitude];
    return FALLBACK_CENTER;
  }, [depot, geoChantiers, geoTeam]);

  const bounds = useMemo(() => {
    const pts = [];
    if (depot?.latitude != null) pts.push([depot.latitude, depot.longitude]);
    geoChantiers.forEach((c) => pts.push([c.latitude, c.longitude]));
    geoTeam.forEach((r) => pts.push([r.latitude, r.longitude]));
    return pts.length >= 2 ? pts : null;
  }, [depot, geoChantiers, geoTeam]);

  return (
    <div
      className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden"
      data-testid="chantiers-map-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <MapTrifold size={16} weight="duotone" className="text-[#7CB2FF]" />
          <span className="text-xs uppercase tracking-widest text-white/70 font-semibold">
            Chantiers en cours
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-white/40">
          {depot?.latitude != null && (
            <span className="flex items-center gap-1">
              <Warehouse size={11} className="text-[#7CB2FF]" weight="fill" /> Siège
            </span>
          )}
          {geoTeam.length > 0 && (
            <span className="flex items-center gap-1" data-testid="chantiers-map-team-count">
              <User size={11} className="text-emerald-400" weight="fill" />
              <span className="text-emerald-400 font-mono">{liveCount}</span>
              <span className="text-white/40">/{totalWithCoords} tech en ligne</span>
            </span>
          )}
          <span data-testid="chantiers-map-count">
            <span className="text-white/70 font-mono">{geoChantiers.length}</span> avec GPS
          </span>
        </div>
      </div>

      {loading ? (
        <div className="h-56 flex items-center justify-center text-white/40 text-xs">Chargement de la carte…</div>
      ) : geoChantiers.length === 0 && !depot?.latitude && geoTeam.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-white/40 text-xs px-6 text-center">
          Aucun chantier actif géolocalisé. Renseigne l&apos;adresse d&apos;un chantier ou configure le siège dans Réglages.
        </div>
      ) : (
        <div style={{ height: 240, width: "100%" }} className="relative">
          <MapContainer
            center={center}
            zoom={11}
            bounds={bounds}
            boundsOptions={{ padding: [40, 40] }}
            style={{ height: "100%", width: "100%", background: "#0A0A0A" }}
            scrollWheelZoom={false}
            attributionControl={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />

            {/* Depot */}
            {depot?.latitude != null && (
              <Marker position={[depot.latitude, depot.longitude]} icon={depotIcon}>
                <Popup>
                  <div style={{ minWidth: 160, color: "#111" }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {depot.name || "Dépôt / Siège"}
                    </div>
                    <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                      {[depot.address, depot.postal_code, depot.city].filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Chantiers */}
            {geoChantiers.map((c) => {
              const color = c.color || STATUS_META[c.status]?.color || "#9CA3AF";
              return (
                <CircleMarker
                  key={c.id}
                  center={[c.latitude, c.longitude]}
                  radius={9}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.55,
                    weight: 2,
                  }}
                  eventHandlers={{ click: () => nav(`/chantiers/${c.id}`) }}
                >
                  <Tooltip direction="top" offset={[0, -8]} opacity={1} sticky>
                    <div style={{ minWidth: 200, color: "#111", padding: "2px 4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            background: color,
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                          aria-hidden
                        >
                          i
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 13 }}>{c.name}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#555" }}>
                        {c.address || "Adresse non renseignée"}
                      </div>
                      {c.city && <div style={{ fontSize: 11, color: "#555" }}>{c.postal_code} {c.city}</div>}
                      <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
                        <span
                          style={{
                            background: `${color}22`,
                            color,
                            padding: "1px 6px",
                            borderRadius: 999,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {STATUS_META[c.status]?.label || c.status}
                        </span>
                        <span style={{ color: "#888" }}>{c.progress || 0}%</span>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 10, color: "#0057cc", fontWeight: 600 }}>
                        Cliquer pour ouvrir la fiche →
                      </div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}

            {/* Techniciens (dernière position connue) */}
            {geoTeam.map((r) => {
              const c = r.color || "#10B981";
              return (
                <CircleMarker
                  key={`tech-${r.id}`}
                  center={[r.latitude, r.longitude]}
                  radius={7}
                  pathOptions={{
                    color: c,
                    fillColor: c,
                    fillOpacity: r.is_live ? 0.9 : 0.35,
                    weight: r.is_live ? 3 : 1,
                  }}
                >
                  <Tooltip direction="top" offset={[0, -6]} opacity={1} sticky>
                    <div style={{ minWidth: 160, color: "#111", padding: "2px 4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: c,
                            boxShadow: r.is_live ? `0 0 8px ${c}` : "none",
                          }}
                          aria-hidden
                        />
                        <span style={{ fontWeight: 700, fontSize: 13 }}>{r.name}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#555", textTransform: "capitalize", marginTop: 2 }}>
                        {r.role}
                      </div>
                      <div style={{ fontSize: 11, marginTop: 4 }}>
                        {r.is_live ? (
                          <span style={{ color: "#059669", fontWeight: 600 }}>● En ligne</span>
                        ) : (
                          <span style={{ color: "#B45309", fontWeight: 600 }}>● Hors ligne</span>
                        )}
                        <span style={{ color: "#888", marginLeft: 6 }}>· {timeAgo(r.updated_at)}</span>
                      </div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Small legend bottom-left */}
          <div className="absolute left-2 bottom-2 z-[1000] flex items-center gap-1.5 rounded-full bg-black/70 border border-white/10 px-2 py-1 text-[9px] uppercase tracking-widest text-white/60 pointer-events-none">
            <Info size={10} weight="duotone" />
            Survole un point
          </div>
        </div>
      )}
    </div>
  );
}
